import { useState } from 'react';
import { Payment, PaymentMethod } from '../types/entities';
import { PaymentFormData } from '../types/forms';
import { ApiError } from '../types/api';
import { getAuthenticatedFetch } from '../utils/api';
import { useErrorHandler } from '../utils/errorHandler';

export const usePaymentsApi = () => {
  const [error, setError] = useState<ApiError | null>(null);
  const { handleApiError: handleError } = useErrorHandler();

  const handleApiError = (err: any): ApiError => {
    const apiError = handleError(err);
    setError(apiError);
    return apiError;
  };

  const clearError = () => setError(null);

  const createPayment = async (paymentData: { method: PaymentMethod; amount: number; soldProductId?: number; appointmentId?: number }): Promise<Payment> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: paymentData.method,
          amount: paymentData.amount,
          soldProductId: paymentData.soldProductId,
          appointmentId: paymentData.appointmentId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const deletePaymentsBySoldProductId = async (soldProductId: number): Promise<void> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const paymentsResponse = await authenticatedFetch(`/payments?soldProductId=${soldProductId}`);
      
      if (!paymentsResponse.ok) {
        return; // No payments or error - just return
      }
      
      const paymentsData = await paymentsResponse.json();
      
      if (paymentsData.payments && paymentsData.payments.length > 0) {
        await Promise.all(paymentsData.payments.map((payment: Payment) =>
          authenticatedFetch(`/payments/${payment.id}`, { method: 'DELETE' })
        ));
      }
    } catch (err) {
      // Silently fail if payments don't exist
      console.warn('Failed to delete payments:', err);
    }
  };

  return {
    error,
    clearError,
    createPayment,
    deletePaymentsBySoldProductId,
  };
};

